// Settings → Brand & workspace → Time zone control (staging only).
// Pure helpers only — no React, no API calls — so they stay trivially
// unit-testable with a fixed Date (never "now" in assertions).

// Feature-detect Intl.supportedValuesOf('timeZone') (Chrome/Firefox/Safari all
// current-gen, but not guaranteed in every runtime, e.g. older WebViews).
// This curated ~30-zone fallback covers the regions our audience is in
// (Europe/CIS-adjacent + the big global hubs) so the picker never renders
// empty on an environment that lacks supportedValuesOf.
const FALLBACK_ZONES = [
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Kyiv',
  'Europe/Warsaw',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Prague',
  'Europe/Vilnius',
  'Europe/Riga',
  'Europe/Tallinn',
  'Europe/Stockholm',
  'Europe/Helsinki',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Mexico_City',
  'America/Sao_Paulo',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Asia/Bangkok',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Africa/Cairo',
  'Africa/Johannesburg',
];

// Normalizes an Intl "GMT+3" / "GMT+03:00" / "GMT" timeZoneName part into a
// consistent "UTC+03:00" / "UTC+00:00" shape, regardless of whether the
// browser gave it to us via the `longOffset` or `shortOffset` style.
function normalizeOffsetString(raw) {
  const rest = String(raw || '').replace(/GMT|UTC/, '').trim();
  if (!rest) return 'UTC+00:00';
  const match = /^([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(rest);
  if (!match) return `UTC${rest}`;
  const [, sign, hh, mm] = match;
  return `UTC${sign}${hh.padStart(2, '0')}:${(mm || '00').padStart(2, '0')}`;
}

/** Try/catch validity check — the only reliable way to know if a string is a
 *  real IANA zone Intl will accept (invalid ids throw at construction). */
export function isValidTimeZone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat(undefined, { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** The visitor's own IANA zone, per the browser/OS. Always falls back to UTC
 *  rather than throwing — this is used as a save-time default. */
export function deviceTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** "UTC+03:00" for the given zone at the given moment (offsets shift across
 *  DST, so this is intentionally a function of `date`, not a static table).
 *  Prefers `longOffset` (always zero-padded HH:MM); falls back to
 *  `shortOffset` for runtimes that don't support the newer style. */
export function formatOffset(tz, date = new Date()) {
  for (const timeZoneName of ['longOffset', 'shortOffset']) {
    try {
      const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName }).formatToParts(date);
      const part = parts.find((p) => p.type === 'timeZoneName');
      if (part && part.value) return normalizeOffsetString(part.value);
    } catch {
      // Invalid tz, or this runtime doesn't support this timeZoneName style —
      // try the next style, or fall through to the UTC default below.
    }
  }
  return 'UTC+00:00';
}

/** All IANA zones grouped by region (the path segment before the first "/",
 *  "UTC" kept as its own single-zone group), each with a human label carrying
 *  the current UTC offset — e.g. "Europe/Kyiv (UTC+03:00)". Sorted
 *  alphabetically within and across groups for a stable, testable order. */
export function listTimeZones(date = new Date()) {
  let zones;
  try {
    zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : null;
  } catch {
    zones = null;
  }
  if (!Array.isArray(zones) || zones.length === 0) zones = FALLBACK_ZONES;
  // Some ICU builds omit the bare "UTC" id from supportedValuesOf() even
  // though Intl.DateTimeFormat accepts it fine — always offer it explicitly
  // rather than depending on ICU coverage. It's the universal safe default
  // used elsewhere in this app (publishing_preferences.timezone defaults to
  // "UTC" server-side when unset).
  if (!zones.includes('UTC')) zones = ['UTC', ...zones];

  const groups = new Map();
  for (const tz of zones) {
    const region = tz === 'UTC' ? 'UTC' : (tz.includes('/') ? tz.split('/')[0] : 'Other');
    if (!groups.has(region)) groups.set(region, []);
    groups.get(region).push({ value: tz, label: `${tz} (${formatOffset(tz, date)})` });
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([region, list]) => ({
      region,
      zones: list.sort((x, y) => x.value.localeCompare(y.value)),
    }));
}

/** "14:05, Tue, 9 Sep" style current-time line for the given zone/locale.
 *  Falls back to UTC/en if the zone or locale is invalid rather than
 *  throwing — this renders in a live-updating UI line, not a form submit. */
export function formatNowIn(tz, locale = 'en', date = new Date()) {
  const safeTz = isValidTimeZone(tz) ? tz : 'UTC';
  const timePart = safeFormat(locale, { timeZone: safeTz, hour: '2-digit', minute: '2-digit', hour12: false }, date);
  const datePart = safeFormat(locale, { timeZone: safeTz, weekday: 'short', day: 'numeric', month: 'short' }, date);
  return `${timePart}, ${datePart}`;
}

function safeFormat(locale, options, date) {
  try {
    return new Intl.DateTimeFormat(locale, options).format(date);
  } catch {
    try {
      return new Intl.DateTimeFormat('en', options).format(date);
    } catch {
      return new Intl.DateTimeFormat('en', { ...options, timeZone: 'UTC' }).format(date);
    }
  }
}
